/**
 * Money — precision-safe currency wrapper around decimal.js-light.
 *
 * Every money value in CrewBit admin is represented as a dollars-as-string
 * value: "150.00", "1500.50", "0.00". NEVER as a JavaScript number, because
 * JavaScript numbers are IEEE 754 floats (0.1 + 0.2 = 0.30000000000000004),
 * and the first time an owner catches their invoice totals off by a cent,
 * trust in the product dies.
 *
 * This module:
 * - Reads dollars-as-string values from the DB / user input.
 * - Performs arithmetic using decimal.js-light internally.
 * - Always returns dollars-as-string values with exactly two decimal places.
 * - Rounds half-up (standard commercial rounding; matches every bank, POS,
 *   and invoice owners have ever seen).
 * - Treats empty string / null / undefined as zero at the boundary.
 * - Single-currency CAD, en-CA locale only for launch.
 *
 * Consumers:
 * - `MoneyInput` component (user input).
 * - Read-only display surfaces (tables, CSV headers) via `formatMoney`.
 * - Arithmetic sites (invoice totals, tax calc, payment allocation) via
 *   the exported arithmetic functions.
 *
 * When we eventually add multi-currency or locale support, the wrapper
 * absorbs the change; consumers do not re-migrate.
 *
 * Rule #34 (Session 65): Money values are dollars-as-string, never JS numbers.
 */

import Decimal from 'decimal.js-light';

// Configure decimal.js-light globally for this module.
// precision: 20 — more than enough for money (CAD max fits in 12 digits).
// rounding: 4 — ROUND_HALF_UP in decimal.js-light's enum. Standard commercial.
Decimal.set({ precision: 20, rounding: 4 });

const ZERO = '0.00';

/**
 * Accepts string (user input, DB-as-text), number (DB numeric deserialized
 * by supabase-js), or null/undefined. Normalization strips currency
 * symbols, thousands separators, and whitespace at the boundary.
 */
export type MoneyInput = string | number | null | undefined;

/**
 * Normalize a raw input value to a Decimal-parseable string.
 * Preserves sign.
 */
function normalize(input: MoneyInput): string {
  if (input === null || input === undefined) return ZERO;
  const trimmed = String(input).trim();
  if (trimmed === '') return ZERO;
  const cleaned = trimmed.replace(/[$,\s]/g, '');
  if (cleaned === '' || cleaned === '-') return ZERO;
  return cleaned;
}

/**
 * Parse a raw money value into a Decimal. Safe for user input, DB reads,
 * and supabase-js-deserialized numbers. Unparseable input falls back to
 * zero (belt-and-suspenders; the UI layer should prevent this reaching
 * arithmetic).
 */
function parseDecimal(input: MoneyInput): Decimal {
  const normalized = normalize(input);
  try {
    return new Decimal(normalized);
  } catch {
    return new Decimal(0);
  }
}

/** Format a Decimal to a dollars-as-string with exactly two decimal places. */
function formatDecimal(d: Decimal): string {
  return d.toFixed(2);
}

/** Round-trip any money input through parse + format. Canonicalizes. */
export function canonicalMoney(input: MoneyInput): string {
  return formatDecimal(parseDecimal(input));
}

/** Add two money strings. Returns a money string. */
export function addMoney(a: string, b: string): string {
  return formatDecimal(parseDecimal(a).plus(parseDecimal(b)));
}

/** Subtract b from a. Returns a money string. */
export function subtractMoney(a: string, b: string): string {
  return formatDecimal(parseDecimal(a).minus(parseDecimal(b)));
}

/** Multiply a money value by a scalar (e.g., quantity). Returns a money string. */
export function multiplyMoney(a: string, scalar: number | string): string {
  const s = typeof scalar === 'number' ? new Decimal(scalar) : parseDecimal(scalar);
  return formatDecimal(parseDecimal(a).times(s));
}

/**
 * Multiply a money value by a percentage (e.g., tax rate of 13 meaning 13%).
 * `percent` is a plain number — 13 for 13%, not 0.13. Returns a money string.
 */
export function multiplyByPercent(a: string, percent: number): string {
  return formatDecimal(
    parseDecimal(a).times(new Decimal(percent)).dividedBy(100)
  );
}

/** Sum an array of money strings. Empty array returns "0.00". */
export function sumMoney(values: string[]): string {
  return formatDecimal(
    values.reduce((acc, v) => acc.plus(parseDecimal(v)), new Decimal(0))
  );
}

/**
 * Format a money string for display. Adds currency symbol and thousands separator.
 *   "1500.00" → "$1,500.00"
 *   "-150.00" → "-$150.00"  (sign before symbol per en-CA convention)
 *   "" or null → "$0.00"
 */
export function formatMoney(value: MoneyInput): string {
  if (value === null || value === undefined) return '$0.00';
  const normalized = canonicalMoney(value);
  const isNegativeVal = normalized.startsWith('-');
  const abs = isNegativeVal ? normalized.slice(1) : normalized;
  const [intPart, decPart] = abs.split('.');
  const withThousands = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${isNegativeVal ? '-' : ''}$${withThousands}.${decPart}`;
}

/**
 * Parse any money input into a canonical money string.
 * Used by MoneyInput on blur. Alias of canonicalMoney for read-site clarity.
 */
export function parseMoney(input: MoneyInput): string {
  return canonicalMoney(input);
}

/** Compare two money values. Returns -1, 0, or 1. */
export function compareMoney(a: string, b: string): number {
  return parseDecimal(a).cmp(parseDecimal(b));
}

/** Is value strictly greater than zero? */
export function isPositive(value: string): boolean {
  return parseDecimal(value).gt(0);
}

/** Is value zero (including "", null, 0, "0", "0.00", etc.)? */
export function isZero(value: MoneyInput): boolean {
  if (value === null || value === undefined) return true;
  return parseDecimal(value).eq(0);
}

/** Is value negative? */
export function isNegative(value: string): boolean {
  return parseDecimal(value).lt(0);
}

/** Negate a money value. "150.00" → "-150.00" */
export function negateMoney(value: string): string {
  return formatDecimal(parseDecimal(value).neg());
}
